// Heatmap collector — click/tap positions, per-view scroll depth, and the
// occasional masked DOM snapshot the admin UI renders heatmaps on.
//
// Everything is gated like session replay: capture only arms once the
// GET /v1/config `heatmaps` block says the app opted in AND the consent gate
// is open AND this visitor is in the stable heatmap cohort — off-cohort
// sessions never add a listener.
//
//   clicks        one capture-phase click listener; each click emits an
//                 `interaction`/`click` event carrying document coordinates
//                 (x normalized to document width 0–10000, y in document px),
//                 the document dimensions, a bounded CSS selector of the
//                 target and the click's offset inside the target box.
//                 Rapid same-selector repeats collapse; a per-view cap bounds
//                 rage-click floods.
//   scroll depth  a rAF-throttled scroll listener tracks the deepest pixel
//                 reached; exactly one `interaction`/`scroll_depth` event is
//                 emitted per view, on view end (tab hide / pagehide / SPA
//                 route change).
//   snapshot      at most once per (page key, device class, day) per client,
//                 the replay rrweb bundle is lazy-loaded with the identical
//                 privacy/masking config replay uses, one Meta+FullSnapshot
//                 pair is captured, recording stops immediately, and the pair
//                 is gzipped and POSTed to /v1/heatmap-snapshot. The server
//                 dedups again and keeps latest-wins per key.
//
// Events carry their own page key (`int_pk`, route name if set else pathname)
// computed at event time — the envelope ctx is captured at flush time, up to
// 5s later, and would misattribute clicks racing an SPA route change.

import { now, uuid, safely, onIdle, supports } from '../safe.js';
import { EVENT_TYPES } from '../schema.js';
import { getIdentity } from '../identity.js';
import { shouldEmit, onConsentChange } from '../consent.js';
import { isHeatmapsEnabled, inHeatmapCohort, onConfigLoaded } from '../config.js';
import { gzip, strBytes } from '../compress.js';

const RRWEB_GLOBAL = '__ambRrwebRecord';
const RRWEB_META = 4;                   // rrweb EventType.Meta
const RRWEB_FULL_SNAPSHOT = 2;          // rrweb EventType.FullSnapshot

const SNAPSHOT_LS_PREFIX = 'amb.hm.snap:';
const SNAPSHOT_IDLE_DELAY_MS = 5000;    // let the page settle before snapshotting
const SNAPSHOT_MAX_GZ_BYTES = 900 * 1024;
const CLICK_DEDUP_MS = 150;             // double-click collapse window
const MAX_CLICKS_PER_VIEW = 50;         // rage-click flood cap
const MAX_SELECTOR_LEN = 256;

let _cfg = null;
let _emit = null;
let _armed = false;

let _clickCount = 0;
let _lastClickAt = 0;
let _lastClickSel = '';

let _maxScrollY = 0;
let _scrollTicking = false;
let _scrollEmitted = false;

export function initHeatmap(cfg, emit) {
  _cfg = cfg || {};
  _emit = emit;
  // Heatmaps only ever turn on via the GET /v1/config `heatmaps` block, which
  // loads async — re-evaluate when the config settles and when consent moves.
  onConfigLoaded(decide);
  onConsentChange(decide);
}

function decide() {
  if (_armed) return;
  if (!isHeatmapsEnabled()) return;       // app/receiver have not enabled heatmaps
  if (!shouldEmit()) return;              // consent not granted — onConsentChange retries
  if (!inHeatmapCohort()) return;         // not sampled — never arm a listener
  _armed = true;
  armClicks();
  armScrollDepth();
  scheduleSnapshot();
}

// ---------------------------------------------------------------------------
// Page identity + document metrics (the shared contract with the receiver)
// ---------------------------------------------------------------------------

function pageKey() {
  const route = typeof _cfg.getRouteName === 'function' ? _cfg.getRouteName() : '';
  if (route) return String(route).slice(0, 512);
  return safely(() => location.pathname) || '/';
}

function docSize() {
  const de = document.documentElement;
  return {
    w: Math.max(1, de.scrollWidth || 0),
    h: Math.max(1, de.scrollHeight || 0)
  };
}

// The shared device-class contract: mobile < 600 <= tablet < 1024 <= desktop
// < 1440 <= wide, over the viewport width. MUST match the receiver's MVs.
function deviceClass() {
  const vw = window.innerWidth || 0;
  return vw < 600 ? 'mobile' : vw < 1024 ? 'tablet' : vw < 1440 ? 'desktop' : 'wide';
}

// ---------------------------------------------------------------------------
// Clicks
// ---------------------------------------------------------------------------

function armClicks() {
  document.addEventListener('click', (e) => safely(() => onClick(e)), { capture: true, passive: true });
}

function onClick(e) {
  if (!shouldEmit()) return;
  if (_clickCount >= MAX_CLICKS_PER_VIEW) return;

  const target = e.target;
  if (!target || !target.getBoundingClientRect) return;
  // Blocked subtrees are holes in the snapshot — their clicks would render as
  // dots on nothing, and the selector could leak DOM structure the host asked
  // us not to record.
  if (isBlocked(target)) return;

  const sel = cssPath(target);
  const t = now();
  if (sel === _lastClickSel && t - _lastClickAt < CLICK_DEDUP_MS) return;
  _lastClickAt = t;
  _lastClickSel = sel;

  const id = getIdentity();
  if (!id) return;

  const d = docSize();
  const pageX = e.pageX != null ? e.pageX : 0;
  const pageY = e.pageY != null ? e.pageY : 0;
  const box = target.getBoundingClientRect();

  _clickCount++;
  _emit({
    id: id.viewId + ':hc:' + uuid(),
    t: t,
    tp: EVENT_TYPES.INTERACTION,
    n: 'click',
    mv: 0, mu: '', mr: '', mnt: '',
    dim: {}, met: {},
    int_pk: pageKey(),
    int_x: clampNorm(pageX / d.w),
    int_y: Math.max(0, Math.round(pageY)),
    int_dw: d.w,
    int_dh: d.h,
    int_sel: sel,
    int_ex: box.width > 0 ? clampNorm((e.clientX - box.left) / box.width) : 0,
    int_ey: box.height > 0 ? clampNorm((e.clientY - box.top) / box.height) : 0
  });
}

function clampNorm(fraction) {
  const v = Math.round(fraction * 10000);
  return v < 0 ? 0 : v > 10000 ? 10000 : v;
}

function isBlocked(el) {
  const p = _cfg.privacy || {};
  const blockClass = p.blockClass || 'amb-block';
  const blockSelector = p.blockSelector || '[data-amb-block]';
  let node = el;
  while (node && node.nodeType === 1) {
    try {
      if (node.classList && node.classList.contains(blockClass)) return true;
      if (node.matches && node.matches(blockSelector)) return true;
    } catch (_) { /* silent */ }
    node = node.parentElement;
  }
  return false;
}

// A bounded, stable-ish CSS path: id short-circuit, class-qualified tag chain
// with :nth-of-type disambiguation, depth <= 5, length <= 256. Same flavor as
// the selectors web-vitals attribution produces (inp_target / lcp_element), so
// element rankings read consistently across features.
function cssPath(el) {
  const parts = [];
  let node = el;
  let depth = 0;
  while (node && node.nodeType === 1 && depth < 5) {
    let part = node.localName || 'unknown';
    if (node.id) {
      parts.unshift(part + '#' + node.id);
      break;
    }
    const cls = classToken(node);
    if (cls) part += '.' + cls;
    const nth = nthOfType(node);
    if (nth > 0) part += ':nth-of-type(' + nth + ')';
    parts.unshift(part);
    node = node.parentElement;
    depth++;
  }
  return parts.join('>').slice(0, MAX_SELECTOR_LEN);
}

function classToken(node) {
  try {
    const c = node.classList && node.classList[0];
    return c ? String(c).slice(0, 40) : '';
  } catch (_) { return ''; }
}

function nthOfType(node) {
  const parent = node.parentElement;
  if (!parent) return 0;
  let i = 0;
  let sameTagBefore = 0;
  let sameTagTotal = 0;
  const children = parent.children;
  for (let j = 0; j < children.length; j++) {
    const c = children[j];
    if (c.localName !== node.localName) continue;
    sameTagTotal++;
    if (c === node) i = sameTagTotal;
    else if (i === 0) sameTagBefore++;
  }
  return sameTagTotal > 1 ? i : 0;
}

// ---------------------------------------------------------------------------
// Scroll depth — one max-depth event per view, on view end
// ---------------------------------------------------------------------------

function armScrollDepth() {
  resetView();

  window.addEventListener('scroll', () => {
    if (_scrollTicking) return;
    _scrollTicking = true;
    requestAnimationFrame(() => {
      _scrollTicking = false;
      const reached = (window.scrollY || 0) + (window.innerHeight || 0);
      if (reached > _maxScrollY) _maxScrollY = reached;
    });
  }, { capture: true, passive: true });

  // View end: tab hide / pagehide / freeze. Capture-phase like the transport's
  // own terminal-flush listeners; the event is synchronously enqueued, so it
  // rides the terminal beacon (or, at worst, the next view's first flush — the
  // event-time int_pk keeps it correctly attributed either way).
  if (supports('visibilityChange')) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') safely(emitScrollDepth)();
    }, true);
  }
  window.addEventListener('pagehide', () => safely(emitScrollDepth)(), true);
  window.addEventListener('freeze', () => safely(emitScrollDepth)(), true);

  // SPA route change: close out the outgoing view, then reset for the new one.
  // Registered here (after initSpa patched history) so the outgoing page key
  // is still the pre-navigation one when our listener runs... location has
  // already changed by the time the synthetic event fires, so int_pk uses the
  // route name when set; path-derived keys attribute to the incoming page —
  // an accepted approximation for path-keyed SPAs.
  const onRouteChange = () => safely(() => { emitScrollDepth(); resetView(); scheduleSnapshot(); })();
  window.addEventListener('amb:locationchange', onRouteChange);
  window.addEventListener('popstate', onRouteChange);
  window.addEventListener('pageshow', (e) => { if (e.persisted) safely(resetView)(); });
}

function resetView() {
  _clickCount = 0;
  _scrollEmitted = false;
  _maxScrollY = (window.scrollY || 0) + (window.innerHeight || 0);
}

function emitScrollDepth() {
  if (_scrollEmitted) return;             // one per view
  if (!shouldEmit()) return;
  const id = getIdentity();
  if (!id) return;

  const d = docSize();
  const maxY = Math.min(_maxScrollY, d.h);
  const pct = Math.max(0, Math.min(100, Math.round((maxY / d.h) * 100)));
  _scrollEmitted = true;
  _emit({
    id: id.viewId + ':hs:' + uuid(),
    t: now(),
    tp: EVENT_TYPES.INTERACTION,
    n: 'scroll_depth',
    mv: 0, mu: '', mr: '', mnt: '',
    dim: {}, met: {},
    int_pk: pageKey(),
    int_y: Math.max(0, Math.round(maxY)),
    int_dh: d.h,
    int_dw: d.w,
    int_sp: pct
  });
}

// ---------------------------------------------------------------------------
// DOM snapshot election — once per (page key, device class, day) per client
// ---------------------------------------------------------------------------

function scheduleSnapshot() {
  safely(() => {
    const urls = resolveUrls();
    if (!urls) return;

    const pk = pageKey();
    const dc = deviceClass();
    const key = SNAPSHOT_LS_PREFIX + pk + ':' + dc;
    const today = new Date().toISOString().slice(0, 10);
    if (supports('localStorage')) {
      try { if (localStorage.getItem(key) === today) return; } catch (_) { /* silent */ }
    }
    if (!supports('compressionStream')) return; // snapshots only ship gzipped

    // Let the page settle so the snapshot shows loaded content, then verify
    // the election again (the user may have navigated away meanwhile).
    setTimeout(() => onIdle(() => safely(() => {
      if (!shouldEmit()) return;
      if (pageKey() !== pk) return;
      captureSnapshot(urls, pk, dc, key, today);
    })()), SNAPSHOT_IDLE_DELAY_MS);
  })();
}

function captureSnapshot(urls, pk, dc, lsKey, today) {
  loadRrweb(urls.scriptUrl, (record) => {
    if (typeof record !== 'function') return;

    const collected = [];
    let stop = null;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      try { if (stop) stop(); } catch (_) { /* silent */ }
      const meta = collected.find((e) => e && e.type === RRWEB_META);
      const full = collected.find((e) => e && e.type === RRWEB_FULL_SNAPSHOT);
      if (!full) return;
      uploadSnapshot(urls, pk, dc, lsKey, today, meta ? [meta, full] : [full]);
    };

    safely(() => {
      // Identical privacy config to replay's buildRrwebConfig — heatmap
      // snapshots can never be less masked than a replay recording.
      const p = _cfg.privacy || {};
      stop = record({
        emit: (event) => {
          collected.push(event);
          if (event && event.type === RRWEB_FULL_SNAPSHOT) finish();
        },
        maskAllInputs: p.maskAllInputs !== false,
        blockClass: p.blockClass || 'amb-block',
        blockSelector: p.blockSelector || '[data-amb-block]',
        maskTextClass: p.maskTextClass || 'amb-mask',
        maskTextSelector: p.maskTextSelector || '[data-amb-mask]',
        ignoreClass: p.ignoreClass || 'amb-ignore',
        inlineStylesheet: true,
        recordCanvas: false,
        collectFonts: false
      });
    })();

    // The FullSnapshot fires synchronously inside record(); this is the
    // belt-and-braces stop for engines where it lands a task later.
    setTimeout(finish, 1000);
  });
}

function uploadSnapshot(urls, pk, dc, lsKey, today, events) {
  let json;
  try { json = JSON.stringify(events); }
  catch (_) { return; }

  gzip(json).then((bytes) => {
    if (!bytes || bytes.length > SNAPSHOT_MAX_GZ_BYTES) return;

    const d = docSize();
    const qs = '?pk=' + encodeURIComponent(pk) +
      '&dc=' + dc +
      '&vw=' + (window.innerWidth || 0) +
      '&vh=' + (window.innerHeight || 0) +
      '&dw=' + d.w + '&dh=' + d.h +
      '&enc=gzip&sv=1' +
      '&env=' + encodeURIComponent(_cfg.environment || 'production') +
      '&url=' + encodeURIComponent(safely(() => location.href.slice(0, 2048)) || '');

    const headers = { 'content-type': 'text/plain' };
    if (_cfg.token) headers['x-amb-token'] = _cfg.token;
    fetch(urls.snapshotUrl + qs, {
      method: 'POST', body: bytes, headers,
      credentials: 'omit', mode: 'cors'
    }).then((res) => {
      // Mark the election done on any accepted/deduped answer so we don't
      // retry all day; a transport error leaves it unmarked for a later view.
      if (res && (res.status === 204 || res.ok)) {
        if (supports('localStorage')) {
          try { localStorage.setItem(lsKey, today); } catch (_) { /* silent */ }
        }
      }
    }).catch(() => { /* silent */ });
  }, () => { /* silent */ });
}

// Derive the snapshot endpoint and the rrweb-bundle URL from the beacon
// endpoint, mirroring replay.js::resolveUrls.
function resolveUrls() {
  const endpoint = _cfg.endpoint;
  if (typeof endpoint !== 'string') return null;
  const i = endpoint.indexOf('/v1/events');
  if (i < 0) return null;
  const base = endpoint.slice(0, i);
  return {
    snapshotUrl: base + '/v1/heatmap-snapshot/' + encodeURIComponent(_cfg.appId),
    scriptUrl: _cfg.replayScriptUrl || (base + '/v1/rum/ambilobe-rum-replay.min.js')
  };
}

function loadRrweb(scriptUrl, cb) {
  if (window[RRWEB_GLOBAL]) { cb(window[RRWEB_GLOBAL]); return; }
  try {
    const s = document.createElement('script');
    s.src = scriptUrl;
    s.async = true;
    if (_cfg.nonce) s.nonce = _cfg.nonce;
    s.addEventListener('load', () => cb(window[RRWEB_GLOBAL] || null));
    s.addEventListener('error', () => cb(null));
    (document.head || document.documentElement).appendChild(s);
  } catch (_) {
    cb(null);
  }
}
